import { gql } from '@apollo/client';

/**
 * What the agents cost.
 *
 * Every model call the platform makes is metered — tokens in, tokens out,
 * cache reads and writes, and the dollars they came to. `llmCostBreakdown`
 * rolls that up by whatever dimensions are asked for: `groupBy: ["day"]` is
 * the trend, `["agent_name"]` is which agent is expensive, `["user_id"]` is
 * who is spending, and an empty list is the single total.
 */
export const LLM_COST_BREAKDOWN = gql`
  query LlmCostBreakdown(
    $groupBy: [String!]!
    $userId: String
    $threadId: String
    $agentName: String
    $sessionId: String
    $modelId: String
    $provider: String
    $sinceHours: Int
    $limit: Int
  ) {
    llmCostBreakdown(
      groupBy: $groupBy
      userId: $userId
      threadId: $threadId
      agentName: $agentName
      sessionId: $sessionId
      modelId: $modelId
      provider: $provider
      sinceHours: $sinceHours
      limit: $limit
    ) {
      bucket { name value }
      calls
      inputTokens
      outputTokens
      cacheReadTokens
      cacheWriteTokens
      reasoningTokens
      totalCostUsd
    }
  }
`;

/** One account's caps and what it has spent against them. */
export const LLM_USER_BUDGET = gql`
  query LlmUserBudget($userId: String!) {
    llmUserBudget(userId: $userId) {
      userId
      monthlyCapUsd
      dailyCapUsd
      spentMonthToDateUsd
      spentLast24hUsd
      note
      updatedBy
    }
  }
`;

/** Every account with a budget, so the desk can see who is over it. */
export const ALL_LLM_USER_BUDGETS = gql`
  query AllLlmUserBudgets($sortBy: String, $onlyOverBudget: Boolean, $limit: Int, $offset: Int) {
    allLlmUserBudgets(sortBy: $sortBy, onlyOverBudget: $onlyOverBudget, limit: $limit, offset: $offset) {
      userId
      monthlyCapUsd
      dailyCapUsd
      spentMonthToDateUsd
      spentLast24hUsd
      overBudget
      note
      updatedBy
      updatedAt
      createdAt
    }
  }
`;

/**
 * What each model costs per million tokens.
 *
 * This is what prices the meter: the spend figures everywhere else are these
 * rates applied to the recorded token counts. Editing a rate therefore changes
 * what every past call is reported to have cost, which is why the screen says
 * so. Cache reads are priced separately and far cheaper, which is what makes
 * the cache hit rate worth watching.
 */
export const LIST_LLM_PRICING = gql`
  query ListLlmPricing($onlyActive: Boolean) {
    listLlmPricing(onlyActive: $onlyActive) {
      modelId
      inputPerM
      outputPerM
      cacheReadPerM
      cacheWritePerM
      isActive
      note
      updatedAt
      updatedBy
      createdAt
    }
  }
`;
